import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, query, handleFirestoreError, OperationType, limit } from '../firebase';
import { Report, EquipmentRow } from '../types';
import { 
  Zap, 
  Search, 
  Filter, 
  Smartphone, 
  Cpu, 
  Activity,
  ChevronRight,
  Monitor,
  QrCode,
  Camera,
  X,
  AlertCircle,
  Calendar,
  Clock,
  ArrowLeft,
  FileText
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const DevicesPage: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // QR Code Scanner State
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<(EquipmentRow & { clientName: string; reportId: string }) | null>(null);

  useEffect(() => {
    const unsubscribeReports = onSnapshot(collection(db, 'reports'), (snapshot) => {
      setReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'reports');
      setLoading(false);
    });

    const unsubscribeSettings = onSnapshot(query(collection(db, 'settings'), limit(1)), (snapshot) => {
      if (!snapshot.empty) {
        setLogoUrl(snapshot.docs[0].data().logoUrl || null);
      }
    });

    return () => {
      unsubscribeReports();
      unsubscribeSettings();
    };
  }, []);

  // Extract all equipment from all reports
  const allDevices = reports.reduce((acc: (EquipmentRow & { clientName: string; reportId: string })[], report) => {
    if (report.equipment) {
      const devices = report.equipment.map(dev => ({
        ...dev,
        clientName: report.clientName || 'Unknown Client',
        reportId: report.id
      }));
      return [...acc, ...devices];
    }
    return acc;
  }, []);

  const filteredDevices = allDevices.filter(device => 
    device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.clientName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Scan Success Handler
  const handleScannedCode = (code: string) => {
    if (!code) return;
    const cleanedCode = code.trim().toUpperCase();
    
    // Find device with matching serial number
    const foundDevice = allDevices.find(dev => 
      (dev.serialNumber || '').trim().toUpperCase() === cleanedCode ||
      (dev.serialNumber || '').trim().toUpperCase().includes(cleanedCode) ||
      cleanedCode.includes((dev.serialNumber || '').trim().toUpperCase())
    );

    if (foundDevice) {
      toast.success(`Success! Device found: ${foundDevice.name}`);
      setSelectedDevice(foundDevice);
      setIsScannerOpen(false);
    } else {
      // Fallback: match by model/name
      const foundByModel = allDevices.find(dev => 
        (dev.model || '').trim().toUpperCase().includes(cleanedCode) ||
        (dev.name || '').trim().toUpperCase().includes(cleanedCode)
      );

      if (foundByModel) {
        toast.success(`Matched device by model: ${foundByModel.name}`);
        setSelectedDevice(foundByModel);
        setIsScannerOpen(false);
      } else {
        toast.error(`Device with code "${code}" not found in our logs.`);
      }
    }
  };

  // Mount Html5QrcodeScanner when modal opens
  useEffect(() => {
    if (!isScannerOpen) return;

    let scanner: Html5QrcodeScanner | null = null;
    
    // Defer initialization to guarantee the element is mounted in the DOM
    const timer = setTimeout(() => {
      try {
        scanner = new Html5QrcodeScanner(
          "qr-reader-container",
          { 
            fps: 10, 
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0
          },
          /* verbose= */ false
        );

        scanner.render(
          (decodedText) => {
            handleScannedCode(decodedText);
          },
          (error) => {
            // Silence silent continuous frame parsing errors
          }
        );
      } catch (err) {
        console.error("Failed to initialize QR code scanner", err);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      if (scanner) {
        scanner.clear().catch(err => {
          console.error("Failed to clear QR scanner", err);
        });
      }
    };
  }, [isScannerOpen]);

  // Find dynamic service history records for a specific device serial number
  const getDeviceServiceHistory = (serialNumber: string) => {
    if (!serialNumber) return [];
    
    return reports.filter(report => {
      return report.equipment && report.equipment.some(eq => 
        (eq.serialNumber || '').trim().toUpperCase() === (serialNumber || '').trim().toUpperCase()
      );
    }).map(report => {
      const eqItem = report.equipment?.find(eq => 
        (eq.serialNumber || '').trim().toUpperCase() === (serialNumber || '').trim().toUpperCase()
      );
      return {
        reportId: report.id,
        clientName: report.clientName || 'Unknown Client',
        createdAt: report.createdAt,
        type: report.type,
        status: report.status,
        deviceStatus: eqItem?.status || 'Unknown',
        findings: report.findings,
        recommendations: report.recommendations,
        comments: report.comments,
        technicianName: report.technicianName || 'Authorized Field Engineer'
      };
    }).sort((a, b) => {
      const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return timeB - timeA;
    });
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading devices...</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-blue-900 rounded-[24px] flex items-center justify-center shadow-xl shadow-blue-900/20 overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
            ) : (
              <Zap className="text-white w-8 h-8" />
            )}
          </div>
          <div>
            <h1 className="text-3xl font-black text-blue-900 tracking-tight">Devices & Equipment</h1>
            <p className="text-slate-500 font-medium uppercase text-[10px] tracking-[0.2em]">Inventory tracking from reports</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, model, serial number or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900/10 transition-all font-medium"
            />
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setIsScannerOpen(true)}
              className="px-6 py-4 bg-blue-800 hover:bg-blue-900 text-white rounded-2xl font-black flex items-center gap-2 transition-all shadow-md hover:shadow-lg cursor-pointer"
            >
              <QrCode className="w-5 h-5 animate-pulse" />
              Scan Device QR
            </button>
            <button className="px-6 py-4 bg-slate-50 text-slate-600 rounded-2xl font-bold flex items-center gap-2 hover:bg-slate-100 transition-colors">
              <Filter className="w-5 h-5" />
              Filters
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDevices.map((device, index) => (
            <motion.div
              key={`${device.reportId}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setSelectedDevice(device)}
              className="group bg-slate-50 p-6 rounded-3xl border border-transparent hover:border-blue-900/10 hover:bg-white hover:shadow-xl hover:shadow-blue-900/5 transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm group-hover:bg-blue-900 group-hover:text-white transition-colors">
                  {device.name.toLowerCase().includes('phone') ? <Smartphone className="w-6 h-6" /> : 
                   device.name.toLowerCase().includes('monitor') ? <Monitor className="w-6 h-6" /> :
                   device.name.toLowerCase().includes('cpu') ? <Cpu className="w-6 h-6" /> :
                   <Activity className="w-6 h-6" />}
                </div>
                <span className={cn(
                  "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest",
                  device.status.toLowerCase() === 'good' ? 'bg-emerald-100 text-emerald-700' :
                  device.status.toLowerCase() === 'faulty' ? 'bg-red-100 text-red-700' :
                  'bg-orange-100 text-orange-700'
                )}>
                  {device.status}
                </span>
              </div>

              <h3 className="text-lg font-black text-blue-900 mb-1 truncate">{device.name}</h3>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-4">{device.model}</p>
              
              <div className="space-y-3 pt-4 border-t border-slate-200/50">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest">S/N</span>
                  <span className="text-xs font-mono font-bold text-slate-700">{device.serialNumber || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest">Client</span>
                  <span className="text-xs font-bold text-blue-900 truncate max-w-[150px]">{device.clientName}</span>
                </div>
              </div>

              <button className="w-full mt-6 py-3 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-blue-900 hover:text-white hover:border-blue-900 transition-all flex items-center justify-center gap-2">
                Service History
                <ChevronRight className="w-3 h-3" />
              </button>
            </motion.div>
          ))}
        </div>

        {filteredDevices.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="w-10 h-10 text-slate-200" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No devices found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* QR Code Scan Modal */}
      <AnimatePresence>
        {isScannerOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsScannerOpen(false)}
              className="absolute inset-0 bg-blue-950/60 backdrop-blur-md"
            />
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[40px] shadow-2xl p-8 overflow-hidden z-10 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 text-blue-900 rounded-xl flex items-center justify-center">
                    <Camera className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-blue-900 tracking-tight">QR Scanner</h2>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Hold device QR code to camera</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsScannerOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-all"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Real Camera Container */}
              <div className="bg-slate-950 rounded-3xl overflow-hidden relative border-4 border-slate-900 aspect-square flex flex-col items-center justify-center text-white mb-6">
                <div id="qr-reader-container" className="w-full h-full" />
                
                {/* Scanner reticle graphics overlay */}
                <div className="absolute inset-0 pointer-events-none border-[30px] border-black/30 flex items-center justify-center">
                  <div className="w-64 h-64 border-2 border-dashed border-blue-500 rounded-2xl relative">
                    {/* Animated scanning line */}
                    <div className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent animate-bounce top-1/2" />
                  </div>
                </div>
              </div>

              {/* Simulation Helper Panel */}
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-800" />
                  <span className="text-xs font-black text-blue-900 uppercase tracking-wider">Simulation Panel</span>
                </div>
                <p className="text-slate-500 text-[11px] leading-relaxed">
                  No printed QR code in hand? Click any of the registered serial numbers below to simulate scanning a device.
                </p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto pr-1">
                  {allDevices.filter(d => d.serialNumber).slice(0, 10).map((dev, i) => (
                    <button
                      key={i}
                      onClick={() => handleScannedCode(dev.serialNumber)}
                      className="px-3 py-1.5 bg-white hover:bg-blue-800 hover:text-white border border-slate-200 hover:border-blue-800 text-slate-700 text-[10px] font-mono rounded-lg transition-all text-left font-bold cursor-pointer"
                    >
                      {dev.serialNumber} ({dev.name.substring(0, 10)}...)
                    </button>
                  ))}
                  {allDevices.filter(d => d.serialNumber).length === 0 && (
                    <p className="text-xs text-slate-400 italic">No device serial numbers logged in reports yet.</p>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Device Service History Detail Modal */}
      <AnimatePresence>
        {selectedDevice && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedDevice(null)}
              className="absolute inset-0 bg-blue-950/60 backdrop-blur-md"
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[40px] shadow-2xl p-8 overflow-hidden z-10 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-900 text-white rounded-2xl flex items-center justify-center">
                    <Activity className="w-6 h-6 animate-pulse" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-blue-900 tracking-tight">{selectedDevice.name}</h2>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Device Metadata & Maintenance History</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedDevice(null)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-all"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Metadata Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                <div className="bg-slate-50 p-4 rounded-2xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Model</p>
                  <p className="text-xs font-black text-slate-800 mt-1 truncate">{selectedDevice.model || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Serial Number</p>
                  <p className="text-xs font-mono font-black text-slate-800 mt-1 truncate">{selectedDevice.serialNumber || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Owner Client</p>
                  <p className="text-xs font-black text-slate-800 mt-1 truncate">{selectedDevice.clientName}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Latest Status</p>
                  <p className="text-xs font-black text-slate-800 mt-1 truncate">
                    <span className={cn(
                      "px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest",
                      selectedDevice.status.toLowerCase() === 'good' ? 'bg-emerald-100 text-emerald-800' :
                      selectedDevice.status.toLowerCase() === 'faulty' ? 'bg-red-100 text-red-800' :
                      'bg-orange-100 text-orange-800'
                    )}>
                      {selectedDevice.status}
                    </span>
                  </p>
                </div>
              </div>

              {/* Maintenance & Report History Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-900" />
                  <h3 className="text-sm font-black text-blue-900 uppercase tracking-wider">Maintenance & Inspection History</h3>
                </div>

                <div className="border-l-2 border-slate-100 pl-6 ml-3 space-y-6">
                  {(() => {
                    const history = getDeviceServiceHistory(selectedDevice.serialNumber);
                    
                    if (history.length === 0) {
                      return (
                        <div className="text-center py-8 bg-slate-50 rounded-2xl -ml-6 border border-slate-100">
                          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-xs font-bold text-slate-500">No previous maintenance reports found for this device</p>
                          <p className="text-[10px] text-slate-400 mt-1">This device has not been logged in any finalized service sheets.</p>
                        </div>
                      );
                    }

                    return history.map((record, index) => {
                      const formattedDate = record.createdAt?.toDate 
                        ? record.createdAt.toDate().toLocaleDateString() 
                        : 'Unknown Date';

                      return (
                        <div key={index} className="relative">
                          <span className="absolute -left-[31px] top-1 bg-white border-4 border-blue-900 rounded-full w-4 h-4 flex items-center justify-center">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                          </span>
                          <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl space-y-3">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-800 text-[9px] font-black uppercase tracking-wider rounded-md">
                                  Report: #{record.reportId.substring(0, 6).toUpperCase()}
                                </span>
                                <span className="text-[10px] text-slate-400 font-bold flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formattedDate}
                                </span>
                              </div>
                              <span className={cn(
                                "px-2 py-0.5 text-[9px] font-black uppercase rounded-md border",
                                record.deviceStatus.toLowerCase() === 'good' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                record.deviceStatus.toLowerCase() === 'faulty' ? 'bg-red-50 text-red-700 border-red-100' :
                                'bg-orange-50 text-orange-700 border-orange-100'
                              )}>
                                Device was {record.deviceStatus}
                              </span>
                            </div>

                            <p className="text-xs text-slate-700">
                              <strong>Inspected By:</strong> {record.technicianName}
                            </p>

                            <div className="space-y-1.5 text-xs">
                              {record.findings && (
                                <p className="text-slate-600 bg-white p-2.5 rounded-lg border border-slate-100 leading-relaxed">
                                  <strong className="text-slate-800 block text-[10px] uppercase tracking-wider mb-0.5">Findings & Observations:</strong>
                                  {record.findings}
                                </p>
                              )}
                              {record.recommendations && (
                                <p className="text-slate-600 bg-white p-2.5 rounded-lg border border-slate-100 leading-relaxed">
                                  <strong className="text-slate-800 block text-[10px] uppercase tracking-wider mb-0.5">Corrective Action Taken / Recommended:</strong>
                                  {record.recommendations}
                                </p>
                              )}
                              {record.comments && (
                                <p className="text-slate-500 bg-white/50 p-2.5 rounded-lg border border-dashed border-slate-200 leading-relaxed">
                                  <strong className="text-slate-700 block text-[10px] uppercase tracking-wider mb-0.5">Comments:</strong>
                                  {record.comments}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DevicesPage;
